import { Head } from "@inertiajs/react"
import { useState } from "react"

import { HpiStatsBar } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface Recipient {
  id: number
  name: string
  email: string
}

interface TaskNotification {
  id: number
  type: string
  task_type: string | null
  due_date: string | null
  case_id: number
  case_name: string | null
  customer_id: number
  customer_name: string | null
  contact_id: number
  contact_name: string | null
  event_id: number
  event_name: string | null
  request: string | null
  assigned_to: string | null
  urgent: boolean
  sensitive: boolean
  escalation_level: string
  to_recipients: Recipient[]
  cc_recipients: Recipient[]
  subject: string
  body: string
}

interface EscalationStep {
  level: string
  label: string
  timing: string
  subject_prefix: string
  admin_cc: boolean
}

interface Props {
  notifications: TaskNotification[]
  notification_count: number
  preview_date: string
  escalation_schedule: EscalationStep[]
}

/* -- Helpers ---------------------------------------------- */

function escalationLabel(level: string): string {
  switch (level) {
    case "before":
      return "Upcoming"
    case "on":
      return "Due Today"
    case "reminder1":
      return "Overdue (24h)"
    case "reminder2":
      return "Overdue (3 days)"
    case "reminder3":
      return "Overdue (7 days)"
    default:
      return level
  }
}

function escalationColor(level: string): string {
  switch (level) {
    case "before":
      return "bg-blue-100 text-blue-800"
    case "on":
      return "bg-amber-100 text-amber-800"
    case "reminder1":
      return "bg-orange-100 text-orange-800"
    case "reminder2":
      return "bg-red-100 text-red-800"
    case "reminder3":
      return "bg-red-200 text-red-900"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

/* -- Breadcrumbs ------------------------------------------ */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Tasks", href: "/marketing_todos" },
  { title: "Notification Preview", href: "/tasks/sample" },
]

/* -- Main Component --------------------------------------- */

export default function NotificationSample({
  notifications,
  notification_count,
  preview_date,
  escalation_schedule,
}: Props) {
  const [showSchedule, setShowSchedule] = useState(false)
  const overdueCount = notifications.filter((n) =>
    ["reminder1", "reminder2", "reminder3"].includes(n.escalation_level),
  ).length

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Sample Task Notifications" />

      <div className="p-6">
        <div className="mb-6 text-center">
          <h2 className="text-hpi-dark-gray text-xl font-semibold">
            Sample Task Notifications
          </h2>
          <div className="text-hpi-med-gray text-sm">
            Preview as of {preview_date}
          </div>
          <div className="mx-auto mt-3 max-w-2xl rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700">
            This is a preview of what task notification emails would be sent by
            the nightly batch job. No emails are sent from this page.
          </div>
        </div>

        <HpiStatsBar
          stats={[
            {
              label: "Pending Notifications",
              subLabel: "Total",
              value: notification_count,
            },
            {
              label: "Overdue",
              subLabel: "CC Admin",
              value: overdueCount,
            },
          ]}
          className="mb-6"
        />

        {/* Escalation Schedule */}
        <div className="border-hpi-light-gray mb-6 rounded-lg border bg-white">
          <button
            type="button"
            onClick={() => setShowSchedule(!showSchedule)}
            className="text-hpi-dark-gray flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold"
          >
            <span>Escalation Schedule</span>
            <svg
              className={`h-4 w-4 transition-transform ${showSchedule ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {showSchedule && (
            <div className="border-hpi-light-gray border-t px-4 py-3">
              <p className="text-hpi-med-blue mb-3 text-xs">
                Tasks are notified on the following schedule. Each level is sent
                once per task. Overdue notifications include admin users
                (coNotify permission) in CC.
              </p>
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-hpi-pale-gray text-hpi-dark-gray border-b text-xs font-semibold">
                    <th className="px-3 py-2">Level</th>
                    <th className="px-3 py-2">Timing</th>
                    <th className="px-3 py-2">Subject Prefix</th>
                    <th className="px-3 py-2">Admin CC</th>
                  </tr>
                </thead>
                <tbody>
                  {escalation_schedule.map((step) => (
                    <tr
                      key={step.level}
                      className="border-hpi-light-gray border-b last:border-0"
                    >
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-[10px] font-semibold ${escalationColor(step.level)}`}
                        >
                          {step.label}
                        </span>
                      </td>
                      <td className="text-hpi-dark-gray px-3 py-2 text-xs">
                        {step.timing}
                      </td>
                      <td className="text-hpi-dark-gray px-3 py-2 text-xs">
                        {step.subject_prefix}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {step.admin_cc ? (
                          <span className="text-hpi-red font-medium">Yes</span>
                        ) : (
                          <span className="text-hpi-med-gray">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {notifications.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue rounded-lg border bg-white p-8 text-center">
            No pending task notifications to send.
          </div>
        )}

        {notifications.length > 0 && (
          <ul className="space-y-4">
            {notifications.map((n) => (
              <li
                key={`${n.type}-${n.id}`}
                className="border-hpi-light-gray overflow-hidden rounded-lg border bg-white"
              >
                {/* Header */}
                <div className="border-hpi-light-gray flex items-start justify-between border-b bg-gray-50 px-4 py-3">
                  <div>
                    {n.case_id > 0 && n.case_name && (
                      <div className="text-hpi-active-blue text-xs font-medium">
                        #{n.case_id} - {n.case_name}
                      </div>
                    )}
                    {n.case_id === 0 &&
                      n.customer_id > 0 &&
                      n.customer_name && (
                        <div className="text-hpi-active-blue text-xs font-medium">
                          Cust #{n.customer_id}: {n.customer_name}
                        </div>
                      )}
                    {n.contact_id > 0 && n.contact_name && (
                      <div className="text-hpi-med-gray text-xs">
                        Contact: {n.contact_name}
                      </div>
                    )}
                    {n.event_id > 0 && n.event_name && (
                      <div className="text-hpi-med-gray text-xs">
                        Event: {n.event_name}
                      </div>
                    )}
                    <div className="text-hpi-dark-gray mt-1 text-sm font-medium">
                      {n.due_date} {n.task_type ? `${n.task_type}: ` : ""}
                      {n.request}{" "}
                      {n.assigned_to && (
                        <span className="text-hpi-med-gray font-normal">
                          for {n.assigned_to}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-2">
                    {n.urgent && (
                      <span className="rounded bg-red-600 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white uppercase">
                        Urgent
                      </span>
                    )}
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-semibold ${escalationColor(n.escalation_level)}`}
                    >
                      {escalationLabel(n.escalation_level)}
                    </span>
                  </div>
                </div>

                {/* Recipients and body */}
                <div className="px-4 py-3">
                  {n.to_recipients.length > 0 && (
                    <div className="mb-2 text-sm">
                      <span className="font-semibold">To:</span>{" "}
                      {n.to_recipients.map((r) => (
                        <span
                          key={r.id}
                          className="text-hpi-dark-gray mr-2 text-xs"
                        >
                          {r.email}
                        </span>
                      ))}
                    </div>
                  )}
                  {n.cc_recipients.length > 0 && (
                    <div className="text-hpi-red mb-2 text-sm">
                      <span className="font-semibold">CC:</span>{" "}
                      {n.cc_recipients.map((r) => (
                        <span key={r.id} className="mr-2 text-xs">
                          {r.email}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-hpi-dark-gray mb-3 text-sm">
                    <span className="font-semibold">Subject:</span> {n.subject}
                  </div>
                  <hr className="border-hpi-light-gray mb-3" />
                  <div
                    className="prose prose-sm text-hpi-dark-gray max-w-none text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: n.body }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  )
}
